/**
 * Состояние тура вне дерева компонентов (E4.6b): тур переживает смену раскладки и
 * переключение учебного контура, при которых страницы размонтируются. Исполнитель
 * (TrainingTourRuntime) и оверлей читают его отсюда. Смена пользователя или компании
 * останавливает тур без возврата: адреса и объекты прежнего пользователя не используются.
 */

import { create } from 'zustand';
import type { TrainingManifest } from '@/api/training';
import { sessionIdentity, useSessionStore } from '@/stores/session';
import type { MapReturnState } from './mapReturnState';
import { IDLE_TOUR, tourReducer, type TourEvent, type TourState } from './tourMachine';

/** Куда вернуть пользователя из тура */
export interface TourReturnState {
	href: string;
	scroll: { x: number; y: number };
	mapState: MapReturnState | null;
	/** data-training-target или id элемента, на котором был фокус */
	focusTarget: string | null;
}

interface TourStore {
	tour: TourState;
	manifest: TrainingManifest | null;
	origin: TourReturnState | null;
	/** Возврат ждёт исполнителя: адрес, прокрутка, карта и фокус */
	returning: TourReturnState | null;
	dispatch: (event: TourEvent) => void;
}

export const useTourStore = create<TourStore>((set) => ({
	tour: IDLE_TOUR,
	manifest: null,
	origin: null,
	returning: null,
	dispatch: (event) => set((state) => ({ tour: tourReducer(state.tour, event) })),
}));

/** Начать урок: экземпляр и точка возврата фиксируются до первого перехода */
export function beginTour(
	event: Extract<TourEvent, { type: 'start' }>,
	manifest: TrainingManifest,
	origin: TourReturnState,
): void {
	useTourStore.setState((state) => {
		const tour = tourReducer(state.tour, event);
		return tour === state.tour ? state : { tour, manifest, origin, returning: null };
	});
}

/** Пауза или закрытие: пользователь возвращается туда, откуда начал */
export function leaveTour(kind: 'pause' | 'close', restore: boolean): void {
	useTourStore.setState((state) => ({
		tour: tourReducer(state.tour, { type: kind }),
		returning: restore ? state.origin : null,
	}));
}

/** Продолжить приостановленный тур: точка возврата -- место, откуда тур продолжили */
export function resumeTour(origin: TourReturnState): void {
	useTourStore.setState((state) => ({
		tour: tourReducer(state.tour, { type: 'resume' }),
		origin,
		returning: null,
	}));
}

export function returnHandled(): void {
	useTourStore.setState({ returning: null });
}

/** Тур прежнего пользователя не продолжается у следующего. Возвращает отписку */
export function bindTourToSessionIdentity(): () => void {
	let identity = sessionIdentity(useSessionStore.getState().user);
	return useSessionStore.subscribe((state) => {
		const next = sessionIdentity(state.user);
		if (next === identity) return;
		identity = next;
		useTourStore.setState({ tour: IDLE_TOUR, manifest: null, origin: null, returning: null });
	});
}
